import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  PricedQuantity,
  ReorderableItem,
} from '../../types/ReorderableItems.types';
import type { BuyItAgainItemContextType } from '../../types/BuyItAgainItemContext.types';

interface BuyItAgainItemProviderProps {
  children: ReactNode;
  item: ReorderableItem;
}

export const BuyItAgainItemContext = createContext<
  BuyItAgainItemContextType | undefined
>(undefined);

export const BuyItAgainItemProvider = ({
  children,
  item,
}: BuyItAgainItemProviderProps) => {
  const {
    productData: { defaultDisplayName },
    merchandisingData: { pricedQuantities, fractionDigits, currencyCode },
    originalOrderedQuantity,
  } = item;

  const defaultPricedQuantity =
    pricedQuantities.find(
      (pricedQty) => pricedQty.quantity === originalOrderedQuantity
    ) || pricedQuantities[0];
  const [selectedPricedQuantity, setSelectedPricedQuantity] = useState<
    PricedQuantity | undefined
  >(defaultPricedQuantity);

  const contextValues = useMemo(
    () => ({
      currencyCode,
      fractionDigits,
      defaultDisplayName,
      pricedQuantities,
      originalOrderedQuantity,
      selectedPricedQuantity,
      setSelectedPricedQuantity,
    }),
    [
      currencyCode,
      fractionDigits,
      defaultDisplayName,
      pricedQuantities,
      originalOrderedQuantity,
      selectedPricedQuantity,
    ]
  );
  return (
    <BuyItAgainItemContext.Provider value={contextValues}>
      {children}
    </BuyItAgainItemContext.Provider>
  );
};

export const useBuyItAgainItemContext = () => {
  const context = useContext(BuyItAgainItemContext);
  if (!context) {
    throw new Error(
      'useBuyItAgainItemContext must be used within BuyItAgainItemProvider'
    );
  }
  return context;
};
